import {
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Popconfirm,
  Popover,
  Row,
  Space,
  Switch,
  Typography,
} from "antd";
import { Calendar, HelpCircle, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Exam, User } from "@types";
import { useDeleteExam, useEditExam } from "@hooks";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getKeycloakRealm } from "../helpers";
import { useCanDeleteExam } from "@/hooks/exam/useCanDeleteExam";

const Paragraph = Typography;

interface Props {
  exam: Exam;
  user: User;
}

export const ExamsListItem = ({ exam, user }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deleteExam = useDeleteExam();
  const editExam = useEditExam();
  
  const [isCheck, setIsCheck] = useState(exam.state === 1);
  const [checkDelete, setCheckDelete] = useState(false);
  const canDeleteExam = useCanDeleteExam(exam.id, checkDelete);

  useEffect(() => {
    setIsCheck(exam.state === 1);
  }, [exam.state]);

  return (
    <div style={{ width: "100%", padding: "8px 4px" }}>
      <Card
        variant={"borderless"}
        style={{ boxShadow: "rgba(0, 0, 0, 0.16) 1px 0px 10px" }}
      >
        <Flex justify="space-between" align="top">
          <Typography.Title level={4} style={{ marginTop: 0 }}>
            {exam.title}
          </Typography.Title>
          <Space>
            <Button
              onClick={() => navigate(`/${getKeycloakRealm}/edit-exam/${exam.id}`)}
              icon={<Pencil />}
            />
            {/* <Button icon={<Calendar />} /> */}
            <Popconfirm
              onOpenChange={(val) => setCheckDelete(val)}
              title={t("pages.examsManagment.deleteExam")}
              description={t("pages.examsManagment.deleteExamDesc")}
              onConfirm={() => deleteExam.mutate(exam.id)}
              okButtonProps={{ loading: deleteExam.isPending || canDeleteExam.isPending, disabled: !canDeleteExam.data }}
              icon={<HelpCircle style={{ color: "red", marginRight: 8 }} />}
            >
              <Button danger icon={<Trash2 />} />
            </Popconfirm>
          </Space>
        </Flex>
        <Flex justify="space-between" align="flex-end">
          <Space.Compact direction="vertical">
            <Paragraph>
              <Typography.Text strong>
                {" "}
                {t("pages.examsManagment.authorFilter")}:{" "}
              </Typography.Text>
              <Typography.Text>{`${exam.owner?.firstName} ${exam.owner?.lastName}`}</Typography.Text>
            </Paragraph>
            <Paragraph>
              <Typography.Text strong>
                {t("pages.examsManagment.categoryFilter")}:{" "}
              </Typography.Text>
              <Space wrap split={<Divider type="vertical" style={{ margin: 0 }} />}>
                {exam.categories.slice(0,3).map((category) => (
                  <Typography.Link key={category.id}>
                    {category.name}
                  </Typography.Link>
                ))}
                {exam.categories.length > 3 &&
                 <Popover content={<Row gutter={[16, 16]}>
                {exam.categories.slice(3).map((_, index) => (
                  (index % 15 === 0) ? (
                    <Col key={index}>
                      <Space direction="vertical" size={2}>
                      {exam.categories.slice(3).slice(index, index + 15).map((cat) => (
                        
                        <Typography.Link key={cat.id}>
                          {cat.name}
                        </Typography.Link>
                        
                      ))}
                      </Space>
                    </Col>
                  ) : null
                ))}
                 </Row>}>
                  <Typography.Link>
                    +{exam.categories.slice(3).length}
                  </Typography.Link>
                  </Popover>}
              </Space>
            </Paragraph>
            <Paragraph>
              <Typography.Text strong>
                {" "}
                {t("pages.examsManagment.statusFilter")}:{" "}
              </Typography.Text>
              <Switch
                loading={editExam.isPending}
                value={isCheck}
                onChange={(val) => {
                  setIsCheck(val);
                  editExam.mutate(
                    { ...exam, state: val ? 1 : 2 },
                    { onError: () => setIsCheck(!val) }
                  );
                }}
                size="small"
              />
            </Paragraph>
          </Space.Compact>
          <Space >
            {exam.isP365Exam && (
              <Badge color={"yellow"} text={t("pages.examsManagment.p365exam")} />
            )}
          {user.applicationRoles.some(item => item.name === "TermAdmin") && (
            <Button type="dashed" size="large" onClick={() => navigate(`/${getKeycloakRealm}/exam-periods/new/${exam.id}`)} icon={<Calendar />}>
            {t("pages.examsManagment.newExamPeriod")}
          </Button>)}
          </Space>
        </Flex>
      </Card>
    </div>
  );
};
